/**
 * The transcript's view of the readout store.
 *
 * `useSyncExternalStore` over one module singleton, so every message row in a
 * long transcript re-renders from the same fact and "one readout at a time" is
 * structural rather than a convention each row has to keep.
 */

import { useSyncExternalStore } from 'react';
import {
  readAloudRowSnapshot,
  readAloudSnapshot,
  subscribeReadAloud,
  subscribeReadAloudRow,
  type ReadAloudRowSnapshot,
  type ReadAloudSnapshot,
} from './player';

/**
 * The WHOLE readout, progress and all. Four updates a second while a voice is
 * sounding, so this belongs only to something small — the player bar itself.
 */
export function useReadAloud(): ReadAloudSnapshot {
  return useSyncExternalStore(subscribeReadAloud, readAloudSnapshot, readAloudSnapshot);
}

/**
 * The row-level view, for the transcript (2026-09-21).
 *
 * The host renders every message row, unmemoised, from whatever this returns.
 * Subscribing it to the full snapshot meant a progress tick re-rendered the
 * entire thread four times a second and every button in the pane answered late.
 * This value keeps its identity across ticks, so the transcript sees nothing
 * until the readout actually moves rows or changes state.
 */
export function useReadAloudRow(): ReadAloudRowSnapshot {
  return useSyncExternalStore(subscribeReadAloudRow, readAloudRowSnapshot, readAloudRowSnapshot);
}

export type { ReadAloudRowSnapshot, ReadAloudSnapshot } from './player';
export {
  dismissReadAloudNotice,
  readAloudKey,
  stopReadAloud,
  stopReadAloudForThread,
  toggleReadAloud,
} from './player';
